export const BACKUP_COLLECTIONS = ['ingredients', 'packaging', 'suppliers', 'purchases', 'recipes', 'batches', 'sales', 'notes', 'reminders', 'notifications', 'settings'];
const REFERENCE_COLLECTIONS = { ingredientId: 'ingredients', recipeId: 'recipes', batchId: 'batches', supplierId: 'suppliers', packagingId: 'packaging' };
const validId = id => typeof id === 'string' && id.length > 0 && !id.includes('/') && id !== '.' && id !== '..';
export const serializeBackupValue = value => {
  if (value && typeof value.toDate === 'function' && typeof value.seconds === 'number') return { __soaplabType: 'timestamp', seconds: value.seconds, nanoseconds: value.nanoseconds || 0 };
  if (value instanceof Date) return { __soaplabType: 'date', value: value.toISOString() };
  if (Array.isArray(value)) return value.map(serializeBackupValue);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).filter(([, item]) => item !== undefined).map(([key, item]) => [key, serializeBackupValue(item)]));
  return value;
};
export const deserializeBackupValue = (value, timestampFactory) => {
  if (Array.isArray(value)) return value.map(item => deserializeBackupValue(item, timestampFactory));
  if (value && typeof value === 'object') {
    if (value.__soaplabType === 'timestamp' || (Object.keys(value).every(key => ['seconds', 'nanoseconds'].includes(key)) && typeof value.seconds === 'number')) return timestampFactory(value.seconds, value.nanoseconds || 0);
    if (value.__soaplabType === 'date') return new Date(value.value);
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, deserializeBackupValue(item, timestampFactory)]));
  }
  return value;
};
export const validateBackup = backup => {
  if (!backup || typeof backup !== 'object' || !backup.collections || Array.isArray(backup.collections) || typeof backup.collections !== 'object') throw new Error('Invalid backup: missing collections object.');
  const unknown = Object.keys(backup.collections).filter(key => !BACKUP_COLLECTIONS.includes(key));
  if (unknown.length) throw new Error(`Unsupported backup collections: ${unknown.join(', ')}.`);
  let total = 0;
  BACKUP_COLLECTIONS.forEach(collection => {
    const rows = backup.collections[collection] || [];
    if (!Array.isArray(rows)) throw new Error(`${collection} must contain a list of documents.`);
    const ids = new Set();
    rows.forEach(row => {
      if (!row || typeof row !== 'object' || Array.isArray(row) || !validId(row.id)) throw new Error(`Every ${collection} document needs a valid original ID.`);
      if (ids.has(row.id)) throw new Error(`Duplicate ${collection} document ID ${row.id}.`);
      ids.add(row.id);
      if (JSON.stringify(row).length > 900000) throw new Error(`${collection}/${row.id} is too large to restore safely.`);
    });
    total += rows.length;
  });
  if (total > 10000) throw new Error('Restore supports at most 10,000 documents per file. Split this backup before importing.');
  return backup;
};
/** Preserve IDs unless they would overwrite a different existing record; remap the entire incoming graph together. */
export const planBackupImport = (backup, existing, makeId) => {
  validateBackup(backup);
  const backupId = String(backup.backupId || `legacy:${backup.exportedAt || 'unknown'}`);
  const mappings = {};
  const collisions = BACKUP_COLLECTIONS.some(collection => (backup.collections[collection] || []).some(row => (existing[collection] || []).some(current => current.id === row.id && !(current.importBackupId === backupId && current.importOriginalId === row.id))));
  BACKUP_COLLECTIONS.forEach(collection => {
    mappings[collection] = {};
    (backup.collections[collection] || []).forEach(row => {
      const alreadyImported = (existing[collection] || []).find(current => current.importBackupId === backupId && current.importOriginalId === row.id);
      mappings[collection][row.id] = alreadyImported?.id || (collisions ? makeId(collection, row.id) : row.id);
    });
  });
  const remap = (value, context = '') => {
    if (Array.isArray(value)) return value.map(item => remap(item, context));
    if (!value || typeof value !== 'object') return value;
    if (value.__soaplabType) return value;
    return Object.fromEntries(Object.entries(value).map(([key, item]) => {
      let collection = REFERENCE_COLLECTIONS[key];
      if (key === 'id' || key === 'firebaseId') collection = value.collection || (context === 'packagingSelection' ? 'packaging' : context === 'batchSnapshot' ? 'batches' : context === 'recipeSnapshot' ? 'recipes' : ['items', 'oils', 'additives', 'waterSelection', 'alkali', 'alkaliSelection'].includes(context) ? 'ingredients' : null);
      if (key === 'alkaliSelection' && typeof item === 'string') collection = 'ingredients';
      if (context === 'alkaliSelections' && typeof item === 'string') collection = 'ingredients';
      const mapped = collection && typeof item === 'string' ? mappings[collection]?.[item] || item : remap(item, key);
      return [key, mapped];
    }));
  };
  const documents = [];
  BACKUP_COLLECTIONS.forEach(collection => (backup.collections[collection] || []).forEach(row => {
    const id = mappings[collection][row.id];
    const { id: originalId, ...data } = row;
    documents.push({ collection, id, data: { ...remap(data), importBackupId: backupId, importOriginalId: originalId }, alreadyImported: (existing[collection] || []).some(current => current.id === id && current.importBackupId === backupId && current.importOriginalId === originalId) });
  }));
  return { backupId, remapped: collisions, mappings, documents };
};
